import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router';

import { ROUTES } from '@constants/routes';

import { useOrderContext } from '../hooks/useOrderContext';
import { clearWizardState } from '../utils/orderStorage';

interface BrowserBackWarningProps {
  onLeave?: () => void;
}

/**
 * Self-contained component that intercepts browser back button navigation
 * and shows a warning modal. Handles all history manipulation and navigation internally.
 */
export const BrowserBackWarning: React.FC<BrowserBackWarningProps> = ({ onLeave }) => {
  const {
    state: { is_rerequisition: isRerequisition },
    partner,
  } = useOrderContext();
  const navigate = useNavigate();
  const [showBackWarning, setShowBackWarning] = useState(false);
  const isBlockingRef = useRef(false);
  const hasInitialPushRef = useRef(false);
  const isHandlingPopStateRef = useRef(false);
  const showBackWarningRef = useRef(false);

  // Helper to build check eligibility route
  const getCheckEligibilityRoute = () => {
    if (isRerequisition) {
      return ROUTES.MY_ORDERS;
    }
    if (partner) {
      return `/${ROUTES.PARTNERSHIP}/${partner}/${ROUTES.ORDER_CHECK}`;
    }
    return `${ROUTES.ORDER}/${ROUTES.ORDER_CHECK}`;
  };

  // Keep ref in sync with state
  useEffect(() => {
    showBackWarningRef.current = showBackWarning;
  }, [showBackWarning]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (_event: PopStateEvent) => {
      // Prevent duplicate handling - check blocking flag, handling flag, and modal visibility
      if (isBlockingRef.current || isHandlingPopStateRef.current || showBackWarningRef.current) {
        return;
      }

      isHandlingPopStateRef.current = true;

      // Restore position by pushing state forward (this does NOT trigger popstate)
      // This prevents the navigation and keeps us on the current page
      window.history.pushState(null, '', window.location.href);
      // Show warning modal
      setShowBackWarning(true);

      // Reset flag after a brief moment to allow next legitimate back press
      setTimeout(() => {
        isHandlingPopStateRef.current = false;
      }, 100);
    };

    // Push initial state ONCE to intercept first back button press
    // This creates a dummy history entry that we can intercept
    if (!hasInitialPushRef.current) {
      window.history.pushState(null, '', window.location.href);
      hasInitialPushRef.current = true;
    }

    // Listen for popstate (back/forward button)
    window.addEventListener('popstate', handlePopState);

    return () => {
      // Cleanup: remove listener to restore normal browser behavior
      window.removeEventListener('popstate', handlePopState);
      // Note: We intentionally do NOT remove the history entry on unmount
      // as that would require history.back() which could navigate away
      // The entry will be naturally cleaned up when user navigates normally
    };
  }, []);

  // Handle modal actions
  const handleStayOnPage = () => {
    // Just close the modal - position is already restored by popstate handler
    // No need to push state again, avoiding extra history entries
    setShowBackWarning(false);
    // Reset handling flag so next back press can be handled
    isHandlingPopStateRef.current = false;
  };

  const handleLeavePage = () => {
    // Clear wizard state before leaving
    clearWizardState();

    setShowBackWarning(false);
    isBlockingRef.current = true;

    // Call optional onLeave callback
    if (onLeave) {
      onLeave();
    }

    // First orders return to eligibility; rerequisitions return to My Orders.
    navigate(getCheckEligibilityRoute(), { replace: true, state: { fromLeaveWizard: true } });

    // Reset flag after navigation
    setTimeout(() => {
      isBlockingRef.current = false;
    }, 100);
  };

  return (
    <Modal show={showBackWarning} onHide={handleStayOnPage} centered>
      <Modal.Header closeButton>
        <Modal.Title>Are you sure you want to leave the page?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Your progress will be lost if you leave this page.</p>
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: 'center' }}>
        <button type='button' className='btn btn-secondary' onClick={handleStayOnPage} style={{ marginRight: '12px' }}>
          Stay on Page
        </button>
        <button type='button' className='btn btn-primary' onClick={handleLeavePage}>
          Leave Page
        </button>
      </Modal.Footer>
    </Modal>
  );
};
